import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Trash2, UserPlus, CheckCircle, XCircle, KeyRound, Mail, Edit2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Define types for users and roles
type UserType = 'admin' | 'investor' | 'entrepreneur';

interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  user_type: UserType;
  created_at: string;
}

const UsersManager = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<UserType>('investor');
  const [invitePending, setInvitePending] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [newName, setNewName] = useState('');
  
  useEffect(() => {
    fetchUsers();
  }, []);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Fetch profiles with user types
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('id, email, full_name, user_type, created_at')
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      if (profiles) {
        setUsers(profiles as unknown as UserProfile[]);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  const handleInviteUser = async () => {
    if (!newUserEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    try {
      setInvitePending(true);
      
      // Check if user already exists
      const { data: existingUsers, error: checkError } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', newUserEmail);
      
      if (checkError) {
        throw checkError;
      }
      
      if (existingUsers && existingUsers.length > 0) {
        toast.error('User with this email already exists');
        return;
      }
      
      // Send invitation
      // In a real app, you would send an email invitation
      // For now, we'll just simulate it by creating a record in profiles
      
      // Generate a random UUID for the user
      const uuid = crypto.randomUUID();
      
      // Create profile record
      const { error: insertError } = await supabase
        .from('profiles')
        .insert([
          {
            id: uuid,
            email: newUserEmail,
            full_name: null, // Will be filled when user completes signup
            user_type: newUserRole
          }
        ]);
      
      if (insertError) {
        throw insertError;
      }
      
      toast.success(`Invitation sent to ${newUserEmail}`);
      setNewUserEmail('');
      fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error('Error inviting user:', error);
      toast.error('Failed to send invitation');
    } finally {
      setInvitePending(false);
    }
  };
  
  const handleRoleChange = async (userId: string, newRole: UserType) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ user_type: newRole })
        .eq('user_id', userId);
      
      if (error) {
        throw error;
      }
      
      setUsers(users.map(user => 
        user.id === userId ? { ...user, user_type: newRole } : user
      ));
      
      toast.success('User role updated successfully');
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
    }
  };
  
  const handleResetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) {
        throw error;
      }
      
      toast.success('Password reset email sent successfully');
    } catch (error) {
      console.error('Error sending password reset:', error);
      toast.error('Failed to send password reset email');
    }
  };

  const handleDeleteUser = async (userId: string, email: string) => {
    if (!confirm(`Are you sure you want to delete user ${email}?`)) {
      return;
    }
    
    try {
      // Delete the profile
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('user_id', userId);
      
      if (error) {
        throw error;
      }
      
      // Update the local state
      setUsers(users.filter(user => user.id !== userId));
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const openEditDialog = (userId: string, currentName: string | null) => {
    setCurrentUserId(userId);
    setNewName(currentName || '');
    setIsEditDialogOpen(true);
  };

  const handleEditUserName = async () => {
    if (!currentUserId) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: newName })
        .eq('id', currentUserId);
      
      if (error) {
        throw error;
      }
      
      // Update the local state
      setUsers(users.map(user => 
        user.id === currentUserId ? { ...user, full_name: newName } : user
      ));
      
      toast.success('User name updated successfully');
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating user name:', error);
      toast.error('Failed to update user name');
    }
  };
  
  return (
    <Card className="bg-card text-card-foreground border-none shadow-none">
      <CardHeader className="pb-3">
        <CardTitle>User Management</CardTitle>
        <CardDescription>Manage users and their roles</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Invite User Form */}
        <div className="mb-6 p-4 rounded-lg bg-secondary/30">
          <h3 className="text-lg font-medium mb-4">Invite New User</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="col-span-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                placeholder="user@example.com"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                className="bg-background border-border"
              />
            </div>
            <div>
              <Label htmlFor="role">Role</Label>
              <Select 
                value={newUserRole} 
                onValueChange={(value: UserType) => setNewUserRole(value)}
              >
                <SelectTrigger id="role" className="bg-background border-border">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="investor">Investor</SelectItem>
                  <SelectItem value="entrepreneur">Entrepreneur</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button 
            className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={handleInviteUser}
            disabled={invitePending || !newUserEmail}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {invitePending ? 'Sending Invite...' : 'Invite User'}
          </Button>
        </div>
        
        {/* Users List */}
        <div className="rounded-lg overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-secondary/30">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center text-card-foreground">Loading users...</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center text-card-foreground">No users found</td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Avatar className="h-8 w-8 mr-3">
                          <AvatarImage src="" alt={user.full_name || user.email} />
                          <AvatarFallback className="bg-primary/10">
                            {(user.full_name ? user.full_name.charAt(0) : user.email.charAt(0)).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-card-foreground">{user.full_name || 'Unnamed User'}</div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Select 
                        value={user.user_type} 
                        onValueChange={(value: UserType) => handleRoleChange(user.id, value)}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="investor">Investor</SelectItem>
                          <SelectItem value="entrepreneur">Entrepreneur</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openEditDialog(user.id, user.full_name)}
                          title="Edit user name"
                        >
                          <Edit2 className="h-4 w-4 text-green-500" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleResetPassword(user.email)}
                          title="Send password reset email"
                        >
                          <Mail className="h-4 w-4 text-blue-500" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteUser(user.id, user.email)}
                          title="Delete user"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Edit User Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update the user's name below.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Name
                </Label>
                <Input
                  id="name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="col-span-3"
                  placeholder="Enter user's full name"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleEditUserName}>Save changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default UsersManager;
